import {
  AlertTriangle,
  GripVertical,
  LayoutGrid,
  Loader2,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  fetchReportChart,
  fetchReportRegistry,
  type ReportChart as ReportChartPayload,
  type ReportChartSpec,
  type ReportRegistry,
} from "../api";
import { Chart, EmptyPanel } from "./report-ui";
import type { SavedChart } from "./saved-charts";

/** Un gráfico guardado, resuelto contra el cliente y las fechas actuales. */
function SavedChartCard({
  saved,
  clientSlug,
  onRemove,
  onDragStart,
  onDrop,
}: {
  saved: SavedChart;
  clientSlug: string;
  onRemove: (id: string) => void;
  onDragStart: (id: string) => void;
  onDrop: (id: string) => void;
}) {
  const [chart, setChart] = useState<ReportChartPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [over, setOver] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setChart(null);
    setError(null);
    fetchReportChart(clientSlug, { ...saved.spec, title: saved.title })
      .then((payload) => { if (!cancelled) setChart(payload); })
      .catch((cause) => {
        if (!cancelled) setError(cause instanceof Error ? cause.message : String(cause));
      });
    return () => { cancelled = true; };
  }, [clientSlug, saved.id, saved.title, JSON.stringify(saved.spec)]);

  return (
    <div
      className={over ? "rep-saved-slot is-over" : "rep-saved-slot"}
      draggable
      onDragStart={(event) => {
        event.dataTransfer.effectAllowed = "move";
        // Firefox exige payload para iniciar el arrastre.
        event.dataTransfer.setData("text/plain", saved.id);
        onDragStart(saved.id);
      }}
      onDragOver={(event) => { event.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(event) => { event.preventDefault(); setOver(false); onDrop(saved.id); }}
    >
      {error ? (
        <EmptyPanel title={saved.title} reason={`No se pudo construir: ${error}`} />
      ) : chart ? (
        <Chart
          chart={chart}
          compact
          actions={
            <>
              <span className="rep-drag-handle" title="Arrastrá para reordenar">
                <GripVertical size={14} aria-hidden="true" />
              </span>
              <button type="button" className="rep-icon-button" title="Quitar de mis gráficos"
                onClick={() => onRemove(saved.id)}>
                <Trash2 size={14} aria-hidden="true" />
              </button>
            </>
          }
        />
      ) : (
        <article className="rep-panel rep-panel-empty">
          <p><Loader2 size={15} className="rep-spin" /> Cargando {saved.title}…</p>
        </article>
      )}
    </div>
  );
}

/** Alta manual de un gráfico, acotada al registro que expone el backend. */
function ChartBuilder({
  registry,
  onAdd,
}: {
  registry: ReportRegistry;
  onAdd: (title: string, spec: ReportChartSpec) => void;
}) {
  const [source, setSource] = useState<"ga4" | "paid">("paid");
  const [metrics, setMetrics] = useState<string[]>(["inversion"]);
  const [dimension, setDimension] = useState("fecha");
  const [days, setDays] = useState(30);
  const [chartType, setChartType] = useState("combo");
  const [title, setTitle] = useState("");

  const availableMetrics = useMemo(
    () => registry.metrics.filter((metric) => metric.source === source),
    [registry, source],
  );
  const availableDimensions = useMemo(
    () => registry.dimensions.filter((item) => item.sources.includes(source)),
    [registry, source],
  );

  // Al cambiar de fuente, métricas y dimensión previas dejan de ser válidas.
  useEffect(() => {
    setMetrics(availableMetrics.length ? [availableMetrics[0].id] : []);
    if (!availableDimensions.some((item) => item.id === dimension)) {
      setDimension(availableDimensions[0]?.id ?? "fecha");
    }
  }, [source]);

  const toggleMetric = (id: string) => {
    setMetrics((current) =>
      current.includes(id)
        ? current.filter((item) => item !== id)
        : current.length >= 3 ? current : [...current, id],
    );
  };

  const defaultTitle = metrics
    .map((id) => registry.metrics.find((metric) => metric.id === id)?.label ?? id)
    .join(" y ");

  return (
    <form
      className="rep-builder"
      onSubmit={(event) => {
        event.preventDefault();
        if (!metrics.length) return;
        onAdd(title.trim() || defaultTitle, {
          metrics, dimension, days, chartType,
          platform: source === "paid" ? null : undefined,
        });
        setTitle("");
      }}
    >
      <div className="rep-builder-row">
        <label>
          <span>Fuente</span>
          <select value={source} onChange={(event) => setSource(event.target.value as "ga4" | "paid")}>
            <option value="paid">Paid (Google Ads + Meta)</option>
            <option value="ga4">Sitio web (GA4)</option>
          </select>
        </label>
        <label>
          <span>Agrupar por</span>
          <select value={dimension} onChange={(event) => setDimension(event.target.value)}>
            {availableDimensions.map((item) => (
              <option key={item.id} value={item.id}>{item.label}</option>
            ))}
          </select>
        </label>
        <label>
          <span>Ventana</span>
          <select value={days} onChange={(event) => setDays(Number(event.target.value))}>
            {registry.days.map((value) => <option key={value} value={value}>{value} días</option>)}
          </select>
        </label>
        <label>
          <span>Tipo</span>
          <select value={chartType} onChange={(event) => setChartType(event.target.value)}>
            {registry.chartTypes.map((value) => <option key={value} value={value}>{value}</option>)}
          </select>
        </label>
      </div>

      <span className="rep-builder-hint">Métricas (hasta 3, todas de la misma fuente)</span>
      <div className="rep-metric-chips">
        {availableMetrics.map((metric) => (
          <button
            key={metric.id}
            type="button"
            className={metrics.includes(metric.id) ? "is-on" : ""}
            onClick={() => toggleMetric(metric.id)}
          >
            {metric.label}
          </button>
        ))}
      </div>

      <div className="rep-builder-row">
        <label className="rep-builder-title">
          <span>Título</span>
          <input value={title} placeholder={defaultTitle}
            onChange={(event) => setTitle(event.target.value)} />
        </label>
        <button type="submit" className="rep-primary-button" disabled={!metrics.length}>
          <Plus size={14} aria-hidden="true" /> Agregar
        </button>
      </div>
    </form>
  );
}

export function ReportChartLibrary({
  clientSlug,
  charts,
  onAdd,
  onRemove,
  onReorder,
}: {
  clientSlug: string;
  charts: SavedChart[];
  onAdd: (title: string, spec: ReportChartSpec) => boolean;
  onRemove: (id: string) => void;
  onReorder: (id: string, targetId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [registry, setRegistry] = useState<ReportRegistry | null>(null);
  const [dragging, setDragging] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    if (!open || registry) return;
    void fetchReportRegistry().then(setRegistry).catch(() => setRegistry(null));
  }, [open, registry]);

  const handleDrop = useCallback((targetId: string) => {
    if (dragging && dragging !== targetId) onReorder(dragging, targetId);
    setDragging(null);
  }, [dragging, onReorder]);

  return (
    <>
      <section className="rep-library">
        <div className="rep-library-head">
          <div>
            <LayoutGrid size={16} aria-hidden="true" />
            <div>
              <strong>Mis gráficos</strong>
              <small>
                {charts.length
                  ? `${charts.length} guardado${charts.length === 1 ? "" : "s"} · se redibujan para el cliente activo · arrastrá para reordenar`
                  : "Guardá un gráfico desde Gertrudis o creá uno acá; quedan disponibles para cualquier cliente."}
              </small>
            </div>
          </div>
          <button type="button" className="rep-primary-button" onClick={() => setOpen(true)}>
            <Plus size={14} aria-hidden="true" /> Agregar gráfico
          </button>
        </div>

        {charts.length > 0 && (
          <div className="rep-saved-grid">
            {charts.map((saved) => (
              <SavedChartCard
                key={saved.id}
                saved={saved}
                clientSlug={clientSlug}
                onRemove={onRemove}
                onDragStart={setDragging}
                onDrop={handleDrop}
              />
            ))}
          </div>
        )}
      </section>

      {open && (
        <div className="rep-drawer-backdrop" role="presentation" onClick={() => setOpen(false)}>
          <aside
            className="rep-drawer"
            role="dialog"
            aria-label="Agregar gráfico"
            onClick={(event) => event.stopPropagation()}
          >
            <header>
              <strong>Agregar un gráfico</strong>
              <button type="button" className="rep-icon-button" aria-label="Cerrar"
                onClick={() => setOpen(false)}>
                <X size={16} aria-hidden="true" />
              </button>
            </header>

            {notice && (
              <p className="rep-note is-parcial">
                <AlertTriangle size={13} aria-hidden="true" /><span>{notice}</span>
              </p>
            )}

            {registry ? (
              <ChartBuilder
                registry={registry}
                onAdd={(title, spec) => {
                  const added = onAdd(title, spec);
                  setNotice(added ? null : "Ese gráfico ya está en tu biblioteca.");
                  if (added) setOpen(false);
                }}
              />
            ) : (
              <p className="rep-empty"><Loader2 size={14} className="rep-spin" /> Cargando métricas disponibles…</p>
            )}

            {charts.length > 0 && (
              <div className="rep-drawer-list">
                <span className="rep-builder-hint">Guardados</span>
                {charts.map((saved) => (
                  <div key={saved.id}>
                    <span>{saved.title}</span>
                    <button type="button" className="rep-icon-button" title="Quitar"
                      onClick={() => onRemove(saved.id)}>
                      <Trash2 size={13} aria-hidden="true" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </aside>
        </div>
      )}
    </>
  );
}
